import { Suspense } from "react";
import { Navigate, Route, useLocation } from "react-router-dom";

import { VerifiedRoute } from "@/components/common/verifiedRoute";
import { lazy } from "@/utils/react/lazyWithRetries";

// Lazy load all major pages for optimal code splitting
const Account = lazy(() => import("@/pages/account"));
const Compare = lazy(() => import("@/pages/compare"));
const PlanWrapper = lazy(() => import("@/pages/plan_unfurled/PlanWrapper"));
const ResultsWrapper = lazy(() => import("@/pages/results_v2/ResultsWrapper"));
const ShopAll = lazy(() => import("@/pages/shopAll"));
const Tests = lazy(() => import("@/pages/tests"));
const TreatmentsRouter = lazy(() => import("@/pages/treatments"));
const WelcomeIntro = lazy(() => import("@/pages/welcome/intro"));
const ResearchConsent = lazy(() => import("@/pages/welcome/researchConsent"));
const VerifyEmail = lazy(() => import("@/pages/welcome/verifyEmail"));

// Reusable page loading component
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="text-center">
      <div className="w-8 h-8 border-4 border-gray-200 border-t-blue-500 rounded-full animate-spin mx-auto mb-4"></div>
    </div>
  </div>
);

const CareRedirect = ({ isMalePartner }: { isMalePartner: boolean }) => {
  const location = useLocation();

  if (
    location.pathname.includes("/consults/") ||
    location.pathname.includes("/intake/") ||
    isMalePartner
  ) {
    const newPath = location.pathname.replace(/^\/care\//, "/treatments/");
    return <Navigate to={newPath} replace />;
  }
  return (
    <Suspense fallback={<PageLoader />}>
      <ShopAll />
    </Suspense>
  );
};

interface ProtectedRoutesProps {
  authState: any;
}

export const ProtectedRoutes = ({ authState }: ProtectedRoutesProps) => {
  const isMalePartner =
    authState.currentUser?.identity?.registrationRoute === "male_partner";

  return (
    <>
      <Route
        path="/tests/*"
        element={
          <VerifiedRoute
            authRequired={true}
            consentRequired={true}
            verifyEmailRequired={true}
          >
            <Suspense fallback={<PageLoader />}>
              <Tests />
            </Suspense>
          </VerifiedRoute>
        }
      />
      <Route
        path="/results/:id/*"
        element={
          <VerifiedRoute
            authRequired={true}
            consentRequired={true}
            verifyEmailRequired={true}
          >
            <Suspense fallback={<PageLoader />}>
              <ResultsWrapper />
            </Suspense>
          </VerifiedRoute>
        }
      />

      <Route
        path="/plan/*"
        element={
          <VerifiedRoute
            authRequired={true}
            consentRequired={true}
            verifyEmailRequired={true}
          >
            <Suspense fallback={<PageLoader />}>
              <PlanWrapper />
            </Suspense>
          </VerifiedRoute>
        }
      />
      <Route
        path="/treatments/*"
        element={
          <VerifiedRoute
            authRequired={true}
            consentRequired={true}
            verifyEmailRequired={true}
          >
            <Suspense fallback={<PageLoader />}>
              <TreatmentsRouter />
            </Suspense>
          </VerifiedRoute>
        }
      />
      <Route
        path="/compare/"
        element={
          <VerifiedRoute
            authRequired={true}
            consentRequired={true}
            verifyEmailRequired={true}
          >
            <Suspense fallback={<PageLoader />}>
              <Compare />
            </Suspense>
          </VerifiedRoute>
        }
      />
      <Route
        path="/account/*"
        element={
          <VerifiedRoute
            authRequired={true}
            consentRequired={true}
            verifyEmailRequired={true}
          >
            <Suspense fallback={<PageLoader />}>
              <Account />
            </Suspense>
          </VerifiedRoute>
        }
      />
      <Route
        path="/care/*"
        element={
          <VerifiedRoute
            authRequired={true}
            consentRequired={true}
            verifyEmailRequired={true}
          >
            <CareRedirect isMalePartner={isMalePartner} />
          </VerifiedRoute>
        }
      />
      <Route
        path="/treatment/"
        element={
          <VerifiedRoute
            authRequired={true}
            consentRequired={true}
            verifyEmailRequired={true}
          >
            <Navigate to={"/treatments/treatment/"} />
          </VerifiedRoute>
        }
      />
      <Route
        path="/verify-email/"
        element={
          <VerifiedRoute authRequired={true}>
            <Suspense fallback={<PageLoader />}>
              <VerifyEmail />
            </Suspense>
          </VerifiedRoute>
        }
      />
      <Route
        path="/welcome/"
        element={
          <VerifiedRoute authRequired={true}>
            <Suspense fallback={<PageLoader />}>
              <WelcomeIntro />
            </Suspense>
          </VerifiedRoute>
        }
      />
      <Route
        path="/research-consent/"
        element={
          <VerifiedRoute authRequired={true}>
            <Suspense fallback={<PageLoader />}>
              <ResearchConsent />
            </Suspense>
          </VerifiedRoute>
        }
      />
    </>
  );
};
